import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/utils/ky';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Home, Loader2, Mic, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  CoachModule,
  CoachSubmodule,
  DEFAULT_COMPANY_NAME,
  DISPLAY_MODULES_KEYWORD,
  ScenarioGenerationMessage,
} from '@/components/coach';
import { StopIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useDeviceId } from '../hooks/useDeviceId';

export const RolePlayCtaMessage = ({
  selectedModule,
  items,
  handleSubmoduleSelect,
  useCase = 'coach',
}: {
  selectedModule: CoachModule | null;
  items: any[];
  handleSubmoduleSelect: (submodule: CoachSubmodule) => void;
  useCase?: string;
}) => {
  const [submodule, setSubmodule] = useState(
    selectedModule?.submodules.length === 1
      ? selectedModule?.submodules[0]
      : null,
  );
  const { t } = useTranslation();
  const { lang } = useParams();

  // Get conversation transcript
  const conversationText = useMemo(
    () =>
      items
        .map(
          (item) =>
            `${item.role}: ${
              item.formatted?.text || item.formatted?.transcript || ''
            }`,
        )
        .join('\n'),
    [items],
  );

  const { mutate: fetchSubmodule, isPending: isFetchingSubmodule } =
    useMutation({
      mutationFn: (data: {
        submodules: any[];
        conversation: string;
        language: string;
      }) =>
        api
          .post(`api/${useCase}/sessions/submodule`, {
            json: data,
            timeout: 9999999,
            retry: {
              limit: 2,
              methods: ['post'],
            },
          })
          .json<{ submodule: any }>(),
      onSuccess: async (data: any) => {
        console.log('fetched submodule:', data);
        setSubmodule(data);
      },
    });

  useEffect(() => {
    if (!submodule) {
      fetchSubmodule({
        submodules: selectedModule?.submodules,
        conversation: conversationText,
        language: lang,
      });
    }
  }, [submodule]);

  return (
    <div className="mt-3 space-y-4">
      {submodule ? (
        <motion.button
          key={submodule.id}
          onClick={() => handleSubmoduleSelect(submodule)}
          className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-primary/60 hover:bg-primary/5 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">
                {useCase === 'coach'
                  ? t(
                      `coach.modules.${selectedModule?.id}.submodules.${submodule.id}.title`,
                    )
                  : submodule.title}
              </h3>
              <p className="text-sm text-gray-500">
                {useCase === 'coach'
                  ? t(
                      `coach.modules.${selectedModule?.id}.submodules.${submodule.id}.description`,
                    )
                  : submodule.description}
              </p>
            </div>
            <span className="ml-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {submodule.type === 'practice'
                ? t('coach.roleplay_cta.practice')
                : t('coach.roleplay_cta.learn')}
            </span>
          </div>
        </motion.button>
      ) : (
        <div className="flex items-start space-x-3">
          <div className="flex-1 bg-white rounded-lg shadow p-4">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <p className="text-sm text-gray-600">
                {t('coach.roleplay_cta.generating')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Chat = ({
  items,
  textInput,
  handleTextInputChange,
  handleTextInputSubmit,
  startRecording,
  stopRecording,
  isRecording,
  isConnected,
  selectedModule,
  handleSubmoduleSelect,
  feedbackMessages,
  handleDiscussFeedback,
  isFeedbackDiscussion,
  isAudioProcessing,
  isLoadingDeviceId,
  useCase = 'sales',
}) => {
  const [recordingTime, setRecordingTime] = useState(0);
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [items, feedbackMessages]);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(
      2,
      '0',
    )}`;
  };

  const renderMessage = (text) => {
    if (text.isGeneratingScenario) {
      return <ScenarioGenerationMessage />;
    }

    let displayModules = false;
    if (text.includes(DISPLAY_MODULES_KEYWORD)) {
      text = text.replace(DISPLAY_MODULES_KEYWORD, '');
      displayModules = true;
    }

    const parts = text.split(/\*\*(.*?)\*\*/g);
    return (
      <>
        {parts.map((part, index) => {
          if (index % 2 === 1) {
            return <strong key={index}>{part}</strong>;
          } else {
            return part;
          }
        })}
        {displayModules && (
          <RolePlayCtaMessage
            selectedModule={selectedModule}
            items={items}
            handleSubmoduleSelect={handleSubmoduleSelect}
            useCase={useCase}
          />
        )}
      </>
    );
  };

  const handlePracticeAgain = () => {
    history.go(-1);
  };

  const renderFeedback = (feedback: any) => {
    return (
      <div>
        <p className="text-gray-700">
          <span dangerouslySetInnerHTML={{ __html: feedback.content }} />
        </p>
        <div className="grid gap-3">
          {feedback.options?.map((option) => (
            <motion.button
              key={option.value}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                'w-full text-left px-4 py-3 rounded-lg border transition-colors',
                'hover:border-primary hover:bg-primary/5',
              )}
              onClick={() => {
                if (option.value === 'practice_again') {
                  handlePracticeAgain();
                } else if (option.value === 'discuss_feedback') {
                  handleDiscussFeedback();
                }
              }}
            >
              <div className="font-medium text-gray-900">{option.label}</div>
              {option.description && (
                <div className="text-sm text-gray-500 mt-1">
                  {option.description}
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </div>
    );
  };

  if (isLoadingDeviceId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-sm text-gray-600">
            {t('coach.loading.message.connecting')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="flex-1 overflow-y-auto w-full mx-auto space-y-4 p-6 pb-24">
        <AnimatePresence>
          {feedbackMessages.length > 0
            ? feedbackMessages.map((feedback, i) => (
                <motion.div
                  key={'feedback-message:' + i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    type: 'spring',
                    stiffness: 50,
                    damping: 20,
                  }}
                  className="flex flex-col items-start"
                >
                  <div
                    className="max-w-[80%] rounded-lg p-4 flex space-x-2 bg-white shadow-sm flex-shrink-0"
                    style={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    <Bot className="min-h-6 min-w-6 text-gray-400" />
                    {renderFeedback(feedback)}
                  </div>
                </motion.div>
              ))
            : items
                .slice(isFeedbackDiscussion ? 2 : 1)
                .filter((item) => !item.formatted?.text)
                .map((item, i) => {
                  const message =
                    item?.formatted?.transcript ||
                    item?.formatted?.text ||
                    item?.content ||
                    '';
                  const isAssistant = item.role === 'assistant';
                  return (
                    <motion.div
                      key={'message:' + i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{
                        type: 'spring',
                        stiffness: 50,
                        damping: 20,
                      }}
                      className={`flex flex-col ${
                        isAssistant ? 'items-start' : 'items-end'
                      }`}
                    >
                      {item?.formatted?.text === '' &&
                      item?.formatted?.transcript === '' ? (
                        <div
                          className={cn(
                            'rounded-lg p-4 flex items-center space-x-2',
                            isAssistant
                              ? 'bg-white shadow-sm'
                              : 'bg-primary/10',
                          )}
                        >
                          {isAssistant && (
                            <Bot className="h-6 w-6 text-gray-400" />
                          )}
                          <div className="flex space-x-1">
                            {[0, 1, 2].map((i) => (
                              <motion.div
                                key={i}
                                className={cn(
                                  'w-2 h-2 rounded-full',
                                  isAssistant ? 'bg-gray-400' : 'bg-primary',
                                )}
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{
                                  duration: 0.6,
                                  repeat: Infinity,
                                  delay: i * 0.2,
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`flex max-w-[75%] p-4 rounded-xl shadow-lg text-sm white-space-pre ${
                            isAssistant
                              ? 'bg-white text-gray-900'
                              : 'bg-primary text-white'
                          }`}
                          style={{
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {isAssistant && (
                            <Bot className="h-5 w-5 mr-3 flex-shrink-0" />
                          )}

                          <div>
                            {isAssistant ? renderMessage(message) : message}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
          {isAudioProcessing && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              // exit={{ opacity: 0, y: -10 }}
              transition={{
                type: 'spring',
                stiffness: 50,
                damping: 20,
              }}
              className={'flex flex-col items-end'}
            >
              <div
                className={
                  'rounded-lg p-4 flex items-center space-x-2 bg-primary/10'
                }
              >
                <div className="flex space-x-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className={'w-2 h-2 rounded-full bg-primary'}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </div>
      {isConnected && (
        <div className="bg-white border-t border-gray-200 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center space-x-4">
              {isRecording && (
                <div className="flex items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{
                        opacity: 1,
                        scale: 1.3,
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        repeatType: 'reverse',
                        ease: 'easeInOut',
                      }}
                      className="h-2 w-2 rounded-full bg-red-600"
                    />
                    <div className="text-md ml-2">
                      {formatDuration(recordingTime)}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-1 relative">
                <textarea
                  id="chat"
                  rows={1}
                  value={textInput}
                  onChange={handleTextInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.shiftKey) {
                        return;
                      }
                      if (textInput.trim() !== '') {
                        handleTextInputSubmit();
                      }
                      e.preventDefault();
                    }
                  }}
                  className="block p-2.5 w-full pr-20 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 dark:bg-gray-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  placeholder={t('coach.chat.inputPlaceholder')}
                  style={{ minHeight: 4 }}
                  disabled={isRecording}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn(
                      'h-8 w-8 p-0 transition-opacity',
                      'opacity-100',
                    )}
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        startRecording();
                      }
                    }}
                  >
                    {isRecording ? (
                      <StopIcon className="h-4 w-4" />
                    ) : (
                      <Mic className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className={cn(
                      'h-8 w-8 p-0 transition-opacity',
                      'opacity-100',
                    )}
                    onClick={handleTextInputSubmit}
                  >
                    <Send className="h-4 w-4 text-gray-500" />
                  </Button>
                </div>
              </div>
            </div>
            {/* <div className="mt-2 text-xs text-gray-500 flex justify-between items-center">
              <span>Press Enter to send</span>
              <span>Voice messages coming soon</span>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingOverlay = ({ state }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const { t } = useTranslation();

  const endingMessages = [
    t('coach.chat.endingMessages.analyzingPerformance'),
    t('coach.chat.endingMessages.reviewActions'),
    t('coach.chat.endingMessages.generatingFeedback'),
    t('coach.chat.endingMessages.finalizingAnalysis'),
  ];

  useEffect(() => {
    let interval;
    if (state === 'ending') {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => (prevIndex + 1) % endingMessages.length);
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [state]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {state === 'connecting'
            ? t('coach.roleplay.connecting')
            : state === 'ending'
            ? t('coach.roleplay.ending')
            : t('coach.chat.loadingScenario')}
        </h2>
        <p className="text-gray-600">
          {state === 'connecting'
            ? t('coach.chat.establishConnection')
            : state === 'ending'
            ? endingMessages[messageIndex]
            : t('coach.chat.prepareSession')}
        </p>
      </div>
    </motion.div>
  );
};

interface SidebarProps {
  userName: string;
  companyName: string;
  activeSessionId?: string;
  onConversationSelect: (sessionId: string) => void;
  useCase?: string;
}

export function Sidebar({
  userName,
  companyName,
  activeSessionId,
  onConversationSelect,
  useCase = 'sales',
}: SidebarProps) {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const deviceId = useDeviceId();

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['conversations', deviceId],
    queryFn: () =>
      api
        .get(`api/${useCase}/conversations`, {
          searchParams: { deviceId },
        })
        .json<
          {
            _id: string;
            title: string;
            lastMessageAt: string;
            messages: {
              _id: string;
              content: string;
              role: string;
            }[];
          }[]
        >(),
    enabled: !!deviceId,
  });

  return (
    <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
      <div className="p-4">
        <div className="h-12 mb-8">
          <img
            src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
            alt="Sales Coach"
            className="h-8"
          />
        </div>

        <nav className="space-y-2">
          <button
            onClick={() =>
              navigate(
                `/${useCase}${
                  searchParams.get('code')
                    ? '?code=' + searchParams.get('code')
                    : ''
                }`,
              )
            }
            className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Home className="h-4 w-4 mr-3" />
            Practice Menu
          </button>
        </nav>
      </div>
      {/* User Profile Section at Bottom */}
      <div className="mt-auto border-t border-gray-200 p-5">
        <div className="flex items-center">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center">
            {userName[0].toUpperCase()}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{userName}</p>
            <p className="text-xs text-gray-500">
              {companyName ?? DEFAULT_COMPANY_NAME}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
